<template>
    <div>
        <PageHeader title="Segurança"></PageHeader>

        <div class="grid grid-cols-1 gap-y-8 max-w-2xl mx-auto mt-6">

            <!-- Alterar Senha -->
            <div class="bg-white rounded-lg shadow p-6">
                <h2 class="text-lg font-semibold text-gray-800 mb-4">Alterar Senha</h2>
                <form @submit.prevent="changePassword" novalidate>
                    <div class="flex flex-col gap-y-4">
                        <BaseInput label="Senha atual" type="password" v-model="passwords.current" :required="true" />
                        <BaseInput label="Nova senha" type="password" v-model="passwords.new" :required="true" />
                        <BaseInput label="Confirmar nova senha" type="password" v-model="passwords.confirm" :required="true" />
                        <div v-if="passwordError" class="text-red-500 text-sm">{{ passwordError }}</div>
                        <div class="flex justify-end">
                            <BaseButton skin="primary" :is-submit="true" :loading="loadingPassword">Salvar nova senha</BaseButton>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Dispositivos conectados -->
            <div class="bg-white rounded-lg shadow p-6">
                <h2 class="text-lg font-semibold text-gray-800 mb-1">Dispositivos conectados</h2>
                <p class="text-sm text-gray-500 mb-4">Máximo de 2 dispositivos simultâneos. O mais antigo é desconectado automaticamente ao fazer login em um terceiro.</p>

                <div v-if="loadingSessions" class="text-sm text-gray-400">Carregando...</div>

                <div v-else class="flex flex-col gap-y-3">
                    <div
                        v-for="session in sessions"
                        :key="session.device_id"
                        class="flex items-center justify-between border border-gray-100 rounded-lg p-4"
                        :class="{ 'border-cyan-400 bg-cyan-50': session.device_id === currentDeviceId }"
                    >
                        <div>
                            <div class="flex items-center gap-x-2">
                                <span class="font-medium text-gray-800">{{ session.device_name || 'Dispositivo desconhecido' }}</span>
                                <span v-if="session.device_id === currentDeviceId" class="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full">Este dispositivo</span>
                            </div>
                            <div class="text-xs text-gray-400 mt-1">
                                Último acesso: {{ formatDate(session.last_used_at) }}
                            </div>
                        </div>
                        <button
                            v-if="session.device_id !== currentDeviceId"
                            @click="logoutDevice(session.device_id)"
                            class="text-sm text-red-500 hover:text-red-700 font-medium"
                        >
                            Deslogar
                        </button>
                        <span v-else class="text-xs text-gray-400">Sessão atual</span>
                    </div>

                    <div v-if="!sessions.length" class="text-sm text-gray-400">
                        Nenhuma sessão ativa encontrada.
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import Swal from 'sweetalert';
import AuthService from '../services/AuthService';
import { SessionService, UserService } from '../services/Entity';

export default {
    name: 'Security',
    setup() {
        const sessions = ref([]);
        const loadingSessions = ref(false);
        const loadingPassword = ref(false);
        const passwordError = ref('');
        const currentDeviceId = AuthService.getOrCreateDeviceId();
        const passwords = ref({ current: '', new: '', confirm: '' });

        onMounted(async () => {
            await fetchSessions();
        });

        async function fetchSessions() {
            try {
                loadingSessions.value = true;
                const { data } = await SessionService.list();
                sessions.value = data;
            } catch (err) {
                console.log(err);
            } finally {
                loadingSessions.value = false;
            }
        }

        async function logoutDevice(deviceId) {
            try {
                await SessionService.logoutDevice(deviceId);
                sessions.value = sessions.value.filter(s => s.device_id !== deviceId);
                Swal({ title: 'Dispositivo desconectado', icon: 'success' });
            } catch (err) {
                Swal({ title: 'Erro ao desconectar dispositivo', icon: 'error' });
            }
        }

        async function changePassword() {
            passwordError.value = '';
            if (!passwords.value.current || !passwords.value.new || !passwords.value.confirm) {
                passwordError.value = 'Preencha todos os campos.';
                return;
            }
            if (passwords.value.new !== passwords.value.confirm) {
                passwordError.value = 'Nova senha e confirmação não coincidem.';
                return;
            }
            try {
                loadingPassword.value = true;
                const auth = AuthService.getAuthInfo();
                await UserService.resetPassword(auth.uuid, {
                    current_password: passwords.value.current,
                    new_password: passwords.value.new,
                });
                passwords.value = { current: '', new: '', confirm: '' };
                Swal({ title: 'Senha alterada com sucesso!', icon: 'success' });
            } catch (err) {
                passwordError.value = err?.response?.data?.text || 'Erro ao alterar senha.';
            } finally {
                loadingPassword.value = false;
            }
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            return new Date(dateStr).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
        }

        return {
            sessions,
            loadingSessions,
            loadingPassword,
            passwordError,
            passwords,
            currentDeviceId,
            logoutDevice,
            changePassword,
            formatDate,
        };
    }
}
</script>
